import React, {Fragment} from "react";
import {useState, useEffect} from "react";
import {FormattedMessage, injectIntl, intlShape} from "react-intl";
import {Modal, Table, Button, Divider} from "antd";
import { DragSource, DropTarget} from "react-dnd";
import update from "immutability-helper";
import {WidgetTypeOptions} from "@models/widget";
import Message from "@constants/message";
import {Widget} from "@models/widget";
import FormattedDateTime from "@components/custom/FormattedDateTime";
import {PaginationDefaultParams} from "@components/custom/Paginator";
import {WidgetLocationOptions} from "@models/widget";
import {NavLink} from "react-router-dom";
import {PlatformOptions} from "../../models";
import __i18n from '../../intl/__i18n';

type Props = {
  data: Widget[];
  loading: boolean;
  deleteFn: (item: Widget) => void;
  intl: intlShape.isRequired;
  isOver: any;
  connectDragSource: any;
  connectDropTarget: any;
  moveRow: any;
  restProps: any;
  updateOrderRequest: any;
};

let dragingIndex = -1;

class BodyRow extends React.Component<Props> {
  constructor(props) {
    super(props);
  }

  render() {
    const {isOver, connectDragSource, connectDropTarget, moveRow, ...restProps} = this.props;
    const style = {...restProps.style, cursor: 'move'};

    let {className}: any = restProps;
    if (isOver) {
      if (restProps.index > dragingIndex) {
        className += ' drop-over-downward';
      }
      if (restProps.index < dragingIndex) {
        className += ' drop-over-upward';
      }
    }

    return connectDragSource(
      connectDropTarget(<tr {...restProps} className={className} style={style}/>),
    );
  }
}


const rowSource = {
  beginDrag(props) {
    dragingIndex = props.index;
    return {
      index: props.index,
    };
  },
};

const rowTarget = {
  drop(props, monitor) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    // Time to actually perform the action
    props.moveRow(dragIndex, hoverIndex);

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex;
  },
};
const DragableBodyRow = DropTarget('row', rowTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
}))(
  DragSource('row', rowSource, connect => ({
    connectDragSource: connect.dragSource(),
  }))(BodyRow),
);


const WidgetsTable: React.FC<Props> = props => {
  useEffect(() => {
    if (data) {
      setItems(data)
    }
  }, [props.data]);
  const {data, loading, currentPage, deleteFn, intl, updateOrderRequest} = props;
  const {formatMessage} = intl;
  const [items, setItems] = useState(props.data)
  const pageOffset = (currentPage - 1) * PaginationDefaultParams.count;

  const components = {
    body: {
      row: DragableBodyRow,
    },
  };

  const moveRow = (dragIndex, hoverIndex) => {
    Modal.confirm({
      content: formatMessage({
        id: "app.drag_drop_warning",
        defaultMessage: Message.Warning.Delete
      }),
      okText: formatMessage({
        id: "app.reorder",
        defaultMessage: "ReOrder"
      }),
      cancelText: formatMessage({
        id: "app.cancel",
        defaultMessage: "Cancel"
      }),
      className: "delete-confirm-dialog",
      onOk: () => reOrderItem(dragIndex, hoverIndex)
    });
  };

  const reOrderItem = (dragIndex, hoverIndex): void => {
    // clone items used for when error occurs and datas must change to last-data
    const clonedItems = Object.assign(items)
    const dragRow = items[dragIndex];
    const orderedItems = update(items, {$splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]]});
    setItems(orderedItems);
    updateOrderRequest({
      payload: {
        clonedItems: clonedItems,
        orderedItems: orderedItems,
        items: orderedItems.map((item, index) => ({
          id: item.id,
          row: index
        }))
      }
    })
  };

  const deleteWidget = (item: Widget): void => {
    Modal.confirm({
      content: formatMessage({
        id: "app.general_delete_warning",
        defaultMessage: Message.Warning.Delete
      }),
      okText: formatMessage({
        id: "app.delete",
        defaultMessage: "Delete"
      }),
      cancelText: formatMessage({
        id: "app.cancel",
        defaultMessage: "Cancel"
      }),
      className: "delete-confirm-dialog",
      onOk: () => deleteFn(item)
    });
  };

  const columns = [
    {
      title: <FormattedMessage id="app.index" defaultMessage={`Index`}/>,
      width: 30,
      dataIndex: "id",
      align: "right",
      key: "id",
      render: (a, b, i) => <span>{i + 1 + pageOffset}</span>
    },
    {
      title: <FormattedMessage id="app.title" defaultMessage={`Title`}/>,
      dataIndex: "display_title",
      key: "title",
      align: "right",
      width: 150,
    },
    {
      title: <FormattedMessage id="app.widget_location" defaultMessage={`Widget Location`}/>,
      width: 150,
      dataIndex: "location",
      key: "location",
      align: "center",
      render: location => {
        return WidgetLocationOptions.find(opt => opt.value == location).label
      }
    },
    {
      title: <FormattedMessage id="app.display_order" defaultMessage={`Display Order`}/>,
      dataIndex: "row",
      key: "row",
      align: "center",
      width: 150,
    },
    {
      title: <FormattedMessage id="app.platforms" defaultMessage={`Platforms`}/>,
      width: 150,
      dataIndex: "owner",
      align: "center",
      key: "owner",
      render: owner => {
        const owners = owner.split(", ");
        return owners.map((key, index) => (
          <Fragment key={key}>
            {PlatformOptions.find(opt => opt.value === key).label}
            {(index !== owners.length - 1) ? "/ " : ""}
          </Fragment>
        ))
      }
    },
    {
      title: <FormattedMessage id="app.type" defaultMessage={`Type`}/>,
      width: 150,
      dataIndex: "type",
      align: "center",
      key: "type",
      render: type => __i18n(`app.${type}`)
    },
    {
      title: (
        <FormattedMessage
          id="app.creation_date"
          defaultMessage={`Creation Date`}
        />
      ),
      align: "center",
      width: 150,
      dataIndex: "created_at",
      key: "created_at",
      render: (text, record) => <FormattedDateTime date={text}/>
    },

    {
      title: "",
      key: "actions",
      align: "left",
      width: 90,
      dataIndex: "actions",
      render: (text, record) => {
        const type = record.type;
        if(type === 'continue_watching') {
          return <></>
        } else {
          return (
              <span>
              <NavLink to={`/widgets/${type}/${record.id}/edit`}>
                <Button type="link" size={`small`}>
                  <FormattedMessage id="app.edit" defaultMessage={`Edit`}/>
                </Button>
              </NavLink>

            <Divider type="vertical" size={`small`}/>
            <Button
                type="link"
                className={`danger`}
                onClick={() => deleteWidget(record)}
            >
              <FormattedMessage id="app.delete" defaultMessage={`Delete`}/>
            </Button>
          </span>
          );
        }
      }
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={items}
      components={components}
      loading={loading}
      pagination={false}
      rowKey='id'
      onRow={(record, index) => ({index, moveRow: moveRow})}
    />
  );
};

export default injectIntl(WidgetsTable);
